'use client'

// ============================================================================
// LandingThemeToggle — переключатель светлой/тёмной темы в шапке лендинга.
// ============================================================================
// Отдельный компонент, а не components/layout/ThemeToggle: тот берёт подписи из
// next-intl, а лендинг рендерится и в /admin/landing/preview, где провайдера
// нет. Подписи — inline-карта локалей, как во всех маркетинговых компонентах.
// ============================================================================

import { useCallback, useSyncExternalStore } from 'react'
import { Moon, Sun } from 'lucide-react'

type Theme = 'light' | 'dark'

const LABEL: Record<string, { toLight: string; toDark: string }> = {
  ru: { toLight: 'Светлая тема', toDark: 'Тёмная тема' },
  ro: { toLight: 'Temă deschisă', toDark: 'Temă întunecată' },
  en: { toLight: 'Light theme', toDark: 'Dark theme' },
  uk: { toLight: 'Світла тема', toDark: 'Темна тема' },
}

// Тему держит класс `dark` на <html> (ставится синхронным скриптом в
// app/layout.tsx). Читаем её как внешний стор, чтобы иконка совпадала с
// реальной темой и на гидрации, и когда класс меняют из другого места.
function subscribe(onChange: () => void) {
  const mo = new MutationObserver(onChange)
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
  return () => mo.disconnect()
}
const getTheme = (): Theme => (document.documentElement.classList.contains('dark') ? 'dark' : 'light')
const getServerTheme = (): Theme => 'light'

export function LandingThemeToggle({ locale }: { locale: string }) {
  const l = LABEL[locale] ?? LABEL.ru
  const theme = useSyncExternalStore(subscribe, getTheme, getServerTheme)

  const toggle = useCallback(() => {
    const next: Theme = getTheme() === 'dark' ? 'light' : 'dark'
    document.documentElement.classList.toggle('dark', next === 'dark')
    try { localStorage.setItem('theme', next) } catch {}
  }, [])

  const label = theme === 'dark' ? l.toLight : l.toDark
  return (
    <button
      type="button"
      onClick={toggle}
      title={label}
      aria-label={label}
      className="inline-flex items-center justify-center h-9 w-9 rounded-lg transition-colors text-ink-500 hover:bg-ink-100 hover:text-ink-900"
    >
      {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  )
}
